// src/components/Orders/ProformaGenerator.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Table,
  Checkbox,
  Button,
  Space,
  Card,
  Row,
  Col,
  message,
  Divider,
} from "antd";
import {
  FilePdfOutlined,
  DownloadOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import vendorSeal from "../assets/seal_nms.jpeg";
import vendorSign from "../assets/vendor_sign.png";
import api from "../services/api";

const { Option } = Select;

const DEBUG_PROFORMA = true;

const dbg = (...args) => {
  if (!DEBUG_PROFORMA) return;
  console.log("[PROFORMA DEBUG]", ...args);
};

// ===============================
// Helper Functions
// ===============================

// Parse capacity from inverter model
const parseInverterCapacity = (modelNo) => {
  if (!modelNo) return "";
  const patterns = [
    /(\d+(\.\d+)?)\s*KW/i,
    /(\d+(\.\d+)?)\s*K\s*W/i,
    /^(\d+(\.\d+)?)\s*/,
    /[^\d]*(\d+(\.\d+)?)[^\d]*$/,
  ];
  for (const pattern of patterns) {
    const match = modelNo.match(pattern);
    if (match) return match[1];
  }
  return "";
};

// Format project type from enum to display value
const formatProjectType = (projectType) => {
  if (!projectType) return "Capex";
  
  const projectTypeMap = {
    Residential: "Capex",
    PMSuryaghar: "PMSuryaghar",
    SocietyPMSuryaghar: "Society PMSuryaghar",
    IndividualPMSuryaghar: "Individual PMSuryaghar",
    Commercial: "Commercial",
    Industrial: "Industrial"
  };
  
  return projectTypeMap[projectType] || projectType;
};

// Connection type display
const getConnectionTypeDisplay = (connectionType) => {
  switch (connectionType) {
    case "SinglePhase":
      return "230 V Single Phase";
    case "ThreePhase":
      return "415 V Three Phase";
    case "Mixed":
      return "Mixed Phase";
    default:
      return "230 V Single Phase";
  }
};

/**
 * Fetch customer details with orders and assets
 */
const fetchCustomerDetails = async (customerId) => {
  try {
    dbg(`Fetching customer details for ID: ${customerId}`);
    const json = await api.get(`/customers/${customerId}`);
    
    if (!json.success || !json.data) {
      dbg("fetchCustomerDetails unexpected json:", json);
      return null;
    }

    const c = json.data;

    const ordersRaw = c.orders || c.order || [];
    const normalizedOrders = (Array.isArray(ordersRaw) ? ordersRaw : [ordersRaw])
      .filter(Boolean)
      .map((o) => ({
        ...o,
        items: o.items || o.orderitem || [],
      }));

    const normalizedAssets = c.assets || c.serializedasset || [];

    const normalizedCustomer = {
      ...c,
      orders: normalizedOrders,
      assets: normalizedAssets,
    };

    return normalizedCustomer;
  } catch (error) {
    dbg("fetchCustomerDetails network error:", error);
    return null;
  }
};

/**
 * Extract available order details from order and assets
 */
const extractAvailableOrderDetails = (order, customerAssets = []) => {
  dbg("extractAvailableOrderDetails START", { 
    orderId: order?.id, 
    assetsCount: customerAssets?.length 
  });

  const details = {
    moduleMake: "",
    moduleModel: "",
    moduleWattage: "",
    numberOfModules: "",
    totalCapacity: "",
    warrantyDetails: "",

    inverterModel: "",
    inverterCapacity: "",
    inverterRating: "",

    mppt: "1 MPPT",
    hpd: "Yes",
    manufacturingYear: "",
    chargeControllerType: "MPPT",

    panelWarrantyYears: order?.panelWarrantyYears?.toString() || "",
    inverterWarrantyYears: order?.inverterWarrantyYears?.toString() || "",
    plantWarrantyYears: order?.plantWarrantyYears?.toString() || "",
  };

  const orderAssets = (customerAssets || []).filter(
    (asset) => asset.orderId === order?.id || asset.customerId === order?.customerId
  );

  const orderItems = order?.items || [];
  
  // From ORDER ITEMS
  if (orderItems && orderItems.length > 0) {
    const inverterItems = orderItems.filter(
      (it) =>
        it.item?.itemType === "Inverter" || 
        it.item?.itemName?.toLowerCase().includes("inverter")
    );

    const solarItems = orderItems.filter(
      (it) =>
        it.item?.itemType === "SolarPanel" ||
        it.item?.itemName?.toLowerCase().includes("solar") ||
        it.item?.itemName?.toLowerCase().includes("panel")
    );

    // Solar Panel Details from Items
    if (solarItems.length > 0) {
      const solarItem = solarItems[0];
      const itemData = solarItem.item || {};

      // Module Make
      if (itemData.itemName && !details.moduleMake) {
        const makeFromName = itemData.itemName.split(" ")[0];
        if (makeFromName && makeFromName.length > 2) details.moduleMake = makeFromName;
      }

      // Module Wattage
      if (!details.moduleWattage) {
        let wattage = 540;
        
        if (itemData.modelNo) {
          const wattageMatch = itemData.modelNo.match(/(\d+)\s*WP?/i);
          if (wattageMatch) wattage = parseInt(wattageMatch[1], 10);
        }

        if (itemData.itemName && wattage === 540) {
          const wattageMatch = itemData.itemName.match(/(\d+)\s*W/);
          if (wattageMatch) wattage = parseInt(wattageMatch[1], 10);
        }

        details.moduleWattage = String(wattage);
      }

      // Get number of modules from quantity
      if (solarItem.quantity) {
        details.numberOfModules = String(solarItem.quantity);
      }
    }

    // Inverter Details from Items
    if (inverterItems.length > 0) {
      const inverterItem = inverterItems[0];
      const itemData = inverterItem.item || {};

      let inverterModelStr = "";

      if (itemData.itemName) {
        inverterModelStr = itemData.itemName;
        
        const capacityMatch = itemData.itemName.match(/(\d+(\.\d+)?)\s*KW/i);
        if (capacityMatch) {
          details.inverterCapacity = capacityMatch[1];
        }
      }

      if (itemData.modelNo) {
        inverterModelStr = inverterModelStr 
          ? `${inverterModelStr} - ${itemData.modelNo}` 
          : itemData.modelNo;
        
        if (!details.inverterCapacity) {
          const capacity = parseInverterCapacity(itemData.modelNo);
          if (capacity) {
            details.inverterCapacity = capacity;
          }
        }
      }

      details.inverterModel = inverterModelStr;
    }
  }

  // From SERIALIZED ASSETS (HIGHER PRIORITY)
  const inverterAssets = orderAssets.filter((asset) => {
    if (asset.item && asset.item.itemType === "Inverter") return true;
    if (asset.brand && asset.brand.toLowerCase().includes("inverter")) return true;
    if (asset.item?.itemName?.toLowerCase().includes("inverter")) return true;
    return false;
  });

  if (inverterAssets.length > 0) {
    const makeModelList = inverterAssets
      .map((a) => {
        const name = a?.item?.itemName?.trim() || a?.brand?.trim() || "Inverter";
        const number = a?.item?.modelNo?.trim() || 
                      a?.batchId?.trim() || 
                      a?.invoiceNumber?.trim() || 
                      a?.serialNumber?.trim() || "";
        return [name, number].filter(Boolean).join(" - ");
      })
      .filter(Boolean);

    const uniqueMakeModels = [...new Set(makeModelList)];
    if (uniqueMakeModels.length > 0) {
      details.inverterModel = uniqueMakeModels.join(", ");
    }

    const inverterAsset = inverterAssets[0];
    
    if (inverterAsset?.purchaseDate && !details.manufacturingYear) {
      details.manufacturingYear = new Date(inverterAsset.purchaseDate).getFullYear().toString();
    }

    if (!details.inverterCapacity) {
      const cap = parseInverterCapacity(
        inverterAsset?.item?.modelNo || 
        inverterAsset?.item?.itemName || 
        inverterAsset?.brand
      );
      if (cap) {
        details.inverterCapacity = cap;
      }
    }
  }

  // Solar Panel Assets
  const solarPanelAssets = orderAssets.filter((asset) => {
    if (asset.item && asset.item.itemType === "SolarPanel") return true;
    if (asset.item?.itemName?.toLowerCase().includes("panel")) return true;
    if (asset.item?.itemName?.toLowerCase().includes("solar")) return true;
    if (asset.brand?.toLowerCase().includes("solar")) return true;
    if (asset.brand?.toLowerCase().includes("premier")) return true;
    return false;
  });

  if (solarPanelAssets.length > 0) {
    const solarPanelAsset = solarPanelAssets.find((asset) => asset.brand) || solarPanelAssets[0];

    // Module Make from brand
    if (solarPanelAsset?.brand && solarPanelAsset.brand.length > 2) {
      details.moduleMake = solarPanelAsset.brand;
    }

    // Module Wattage from asset
    if (!details.moduleWattage) {
      let wattage = null;

      if (solarPanelAsset?.capacity) {
        const cap = parseInt(String(solarPanelAsset.capacity), 10);
        if (Number.isFinite(cap) && cap > 0) wattage = String(cap);
      }
      
      if (!wattage && solarPanelAsset?.item?.modelNo) {
        const match = solarPanelAsset.item.modelNo.match(/(\d+)\s*WP?/i);
        if (match) wattage = match[1];
      }
      
      if (!wattage && solarPanelAsset?.item?.itemName) {
        const match = solarPanelAsset.item.itemName.match(/(\d+)\s*W/i);
        if (match) wattage = match[1];
      }
      
      if (!wattage && solarPanelAsset?.model) {
        const match = solarPanelAsset.model.match(/(\d+)\s*WP?/i);
        if (match) wattage = match[1];
      }
      
      if (wattage) {
        details.moduleWattage = String(wattage);
      }
    }

    // Use asset count for modules
    details.numberOfModules = solarPanelAssets.length.toString();
  }

  // Fallback to order items if no assets
  if (solarPanelAssets.length === 0 && orderItems && orderItems.length > 0) {
    const solarItems = orderItems.filter(
      (it) =>
        it.item?.itemType === "SolarPanel" ||
        it.item?.itemName?.toLowerCase().includes("solar") ||
        it.item?.itemName?.toLowerCase().includes("panel")
    );

    if (solarItems.length > 0) {
      const solarItem = solarItems[0];
      if (solarItem.quantity) {
        details.numberOfModules = String(solarItem.quantity);
      }
    }
  }

  // Calculate total capacity
  if (details.moduleWattage && details.numberOfModules) {
    const wattage = parseInt(details.moduleWattage, 10) || 540;
    const modules = parseInt(details.numberOfModules, 10) || 1;
    details.totalCapacity = ((modules * wattage) / 1000).toFixed(3);
  }

  // Inverter Rating from connection type
  if (order?.connectionType && !details.inverterRating) {
    details.inverterRating = getConnectionTypeDisplay(order.connectionType);
  }

  // Warranty Details
  if (order?.panelWarrantyYears > 0 || order?.inverterWarrantyYears > 0 || order?.plantWarrantyYears > 0) {
    details.warrantyDetails =
      `Panel: ${order.panelWarrantyYears || 0} years, ` +
      `Inverter: ${order.inverterWarrantyYears || 0} years, ` +
      `Plant: ${order.plantWarrantyYears || 0} years`;
  }

  // Set defaults if still empty
  if (!details.moduleWattage) details.moduleWattage = "540";
  if (!details.numberOfModules) details.numberOfModules = "6";
  if (!details.inverterModel) details.inverterModel = "Growatt 3KW TL - X2 (Pro)";
  if (!details.inverterCapacity) details.inverterCapacity = "3";

  return details;
};

// Image to Data URL helper
const imageUrlToDataUrl = async (url) => {
  try {
    if (typeof url === "string" && url.startsWith("data:")) return url;

    const response = await fetch(url);
    if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

    const blob = await response.blob();
    
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    dbg("imageUrlToDataUrl error:", err);
    return null;
  }
};

const ProformaGenerator = ({ visible, onCancel, orders, customers }) => {
  const [form] = Form.useForm();
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [customerAssets, setCustomerAssets] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [signImageData, setSignImageData] = useState(null);
  const [sealImageData, setSealImageData] = useState(null);

  // ===============================
  // FIXED VALUES
  // ===============================
  const FIXED = useMemo(
    () => ({
      reArrangementType: "Net metering Arrangement",
      reSource: "Solar",
      installedCapacityRooftopGround: "NA",
      installedCapacityGround: "NA",
    }),
    []
  );

  // Load signature and seal images on component mount
  useEffect(() => {
    const loadImages = async () => {
      try {
        const signData = await imageUrlToDataUrl(vendorSign);
        const sealData = await imageUrlToDataUrl(vendorSeal);
        setSignImageData(signData);
        setSealImageData(sealData);
      } catch (error) {
        dbg("Error loading images:", error);
      }
    };
    loadImages();
  }, []);

  // ===============================
  // Fetch assets when customer changes
  // ===============================
  useEffect(() => {
    if (!selectedCustomer?.id) {
      setCustomerAssets([]);
      return;
    }

    const loadAssets = async () => {
      setLoadingAssets(true);
      try {
        const data = await api.get(`/serialized-assets?customerId=${selectedCustomer.id}`);
        if (data.success && Array.isArray(data.data)) {
          setCustomerAssets(data.data);
        } else if (Array.isArray(data)) {
          setCustomerAssets(data);
        } else {
          setCustomerAssets([]);
        }
      } catch (error) {
        dbg("Error loading assets:", error);
        setCustomerAssets([]);
      }
      setLoadingAssets(false);
    };

    loadAssets();
  }, [selectedCustomer]);

  // ===============================
  // Auto-fill on customer + order select
  // ===============================
  useEffect(() => {
    if (!selectedCustomer || selectedOrders.length === 0) return;

    const order = selectedOrders[0];
    
    // Get plant capacity
    const plantCapacity = order?.plantSizeKw || "3";
    const plantCapacityStr = plantCapacity ? String(plantCapacity) : "3";
    
    // Get installation date
    const installationDate = order?.installationCompletedDate || 
                            order?.orderDate || 
                            dayjs();
    
    // Get project type and format it
    const projectType = order?.projectType;
    const formattedProjectType = formatProjectType(projectType);
    
    // Extract details
    const availableDetails = extractAvailableOrderDetails(order, customerAssets);
    
    // Calculate module capacity
    const moduleCapacity = plantCapacityStr ? `${plantCapacityStr}KW` : "3KW";

    const next = {
      // Customer fields
      customerName: selectedCustomer.customerName || "",
      consumerNo: order?.selectedConsumerNumber || selectedCustomer.consumerNo || "",
      mobileNumber: selectedCustomer.customerMobile || selectedCustomer.mobile || "",
      email: selectedCustomer.customerEmail || selectedCustomer.email || "",
      address: selectedCustomer.customerAddress || selectedCustomer.address || "",
      pincode: selectedCustomer.pincode || selectedCustomer.pinCode || "",
      district: selectedCustomer.district || "",
      state: selectedCustomer.state || "Maharashtra",

      // Fixed values
      reArrangementType: FIXED.reArrangementType,
      reSource: FIXED.reSource,
      projectModel: "Capex",
      installedCapacityRooftopGround: FIXED.installedCapacityRooftopGround,
      installedCapacityGround: FIXED.installedCapacityGround,

      // Capacities
      sanctionCapacity: plantCapacityStr,
      installedCapacity: plantCapacityStr,

      // Date
      installationDate: dayjs(installationDate),

      // Extracted details
      moduleMake: availableDetails.moduleMake || "Premier",
      moduleModel: availableDetails.moduleModel || "",
      moduleWattage: availableDetails.moduleWattage || "540",
      numberOfModules: availableDetails.numberOfModules || "6",
      totalCapacity: availableDetails.totalCapacity || plantCapacityStr,
      warrantyDetails: availableDetails.warrantyDetails || "",
      
      inverterMaker: availableDetails.inverterModel,
      inverterCapacity: availableDetails.inverterCapacity || "3",
      inverterRating: availableDetails.inverterRating || "230 V Single Phase",
      
      mppt: availableDetails.mppt,
      hpd: availableDetails.hpd,
      manufacturingYear: availableDetails.manufacturingYear || "2026",
      
      panelWarrantyYears: availableDetails.panelWarrantyYears,
      inverterWarrantyYears: availableDetails.inverterWarrantyYears,
      plantWarrantyYears: availableDetails.plantWarrantyYears,
      
      // For Solar PV Details section
      noOfPVModules: availableDetails.numberOfModules || "6",
      moduleCapacity: moduleCapacity,
      
      // Inverter serial
      inverterSerial: "0SQQD27V26D800YD",
    };
    
    form.setFieldsValue(next);
    
  }, [selectedCustomer, selectedOrders, form, FIXED, customerAssets]);

  const handleOrderSelect = async (orderId, checked) => {
    if (checked) {
      const order = orders.find((o) => o.id === orderId);
      if (!order) return;

      setSelectedOrders((prev) => [...prev, order]);

      if (!selectedCustomer && order.customerId) {
        setLoadingAssets(true);
        
        let customer = customers.find((c) => c.id === order.customerId);
        
        if (!customer) {
          const detailedCustomer = await fetchCustomerDetails(order.customerId);
          if (detailedCustomer) {
            customer = detailedCustomer;
          }
        }
        
        setLoadingAssets(false);
        
        if (customer) {
          setSelectedCustomer(customer);
          
          const customerOrder = customer.orders?.find(o => o.id === orderId);
          if (customerOrder) {
            setSelectedOrders([customerOrder]);
          }
        }
      }
    } else {
      setSelectedOrders((prev) => prev.filter((o) => o.id !== orderId));
      
      if (selectedOrders.length === 1) {
        setSelectedCustomer(null);
        setCustomerAssets([]);
        form.resetFields();
      }
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedOrders(orders || []);
      if (orders?.length > 0 && orders[0].customerId) {
        const customer = customers.find((c) => c.id === orders[0].customerId);
        if (customer) {
          setSelectedCustomer(customer);
        }
      }
    } else {
      setSelectedOrders([]);
      setSelectedCustomer(null);
      setCustomerAssets([]);
      form.resetFields();
    }
  };

  const generatePDF = async () => {
    try {
      setGenerating(true);
      
      const values = form.getFieldsValue(true);
      
      const data = {
        ...values,
        ...FIXED,
        installationDate: values.installationDate
          ? values.installationDate.format("DD/MM/YYYY")
          : dayjs().format("DD/MM/YYYY"),
        generatedDate: dayjs().format("DD/MM/YYYY HH:mm"),
      };

      // Create PDF
      const doc = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();

      const marginL = 15;
      const marginR = 15;
      const usableW = pageW - marginL - marginR;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);

      // Column widths
      const col0 = 20;  // Sr.No.
      const col1 = 80;  // Particulars
      const col2 = usableW - col0 - col1;  // As Commissioned

      const commonCell = {
        fontSize: 10,
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.2,
        cellPadding: 4,
        valign: "middle",
        overflow: "linebreak",
      };

      const headStyle = {
        ...commonCell,
        fontStyle: "bold",
        halign: "center",
        fillColor: [240, 240, 240],
      };

      // Format address
      const addressParts = [
        data.address,
        data.district ? `District: ${data.district}` : "",
        data.state ? `State: ${data.state}` : "",
        data.pincode ? `Pin Code: ${data.pincode}` : ""
      ].filter(Boolean);
      const addressFull = addressParts.join(", ");

      // ==================== PAGE 1: Annexure-I ====================
      
      // Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Renewable Energy Generating System", pageW / 2, 20, { align: "center" });
      
      doc.setFontSize(14);
      doc.text("Annexure-I", pageW / 2, 28, { align: "center" });
      
      doc.setFontSize(12);
      doc.text("Commissioning Report for RE System", pageW / 2, 36, { align: "center" });

      // Prepare table body
      const bodyRows = [
        // Header
        [
          { content: "Sr.No.", styles: headStyle },
          { content: "Particulars", styles: headStyle },
          { content: "As Commissioned", styles: headStyle }
        ],
        
        // Row 1
        [
          { content: "1", styles: { ...commonCell, halign: "center" } },
          { content: "Name of The Consumer", styles: { ...commonCell, fontStyle: "bold" } },
          { content: data.customerName || "", styles: { ...commonCell, fontStyle: "bold" } }
        ],
        
        // Row 2
        [
          { content: "2", styles: { ...commonCell, halign: "center" } },
          { content: "Consumer number", styles: { ...commonCell, fontStyle: "bold" } },
          { content: data.consumerNo || "", styles: { ...commonCell, fontStyle: "bold" } }
        ],
        
        // Row 3
        [
          { content: "3", styles: { ...commonCell, halign: "center" } },
          { content: "Mobile Number", styles: { ...commonCell, fontStyle: "bold" } },
          { content: data.mobileNumber || "", styles: { ...commonCell, fontStyle: "bold" } }
        ],
        
        // Row 4
        [
          { content: "4", styles: { ...commonCell, halign: "center" } },
          { content: "E-mail", styles: { ...commonCell, fontStyle: "bold" } },
          { content: data.email || "", styles: { ...commonCell, fontStyle: "bold" } }
        ],
        
        // Row 5
        [
          { content: "5", styles: { ...commonCell, halign: "center" } },
          { content: "Address Of Installation", styles: { ...commonCell, fontStyle: "bold" } },
          { content: addressFull || "",
            styles: { ...commonCell, fontStyle: "bold" } }
        ],
        
        // Row 6
        [
          { content: "6", styles: { ...commonCell, halign: "center" } },
          { content: "RE Arrangement Type", styles: { ...commonCell } },
          { content: data.reArrangementType, styles: { ...commonCell } }
        ],
        
        // Row 7
        [
          { content: "7", styles: { ...commonCell, halign: "center" } },
          { content: "RE Source", styles: { ...commonCell } },
          { content: data.reSource, styles: { ...commonCell } }
        ],
        
        // Row 8
        [
          { content: "8", styles: { ...commonCell, halign: "center" } },
          { content: "Sanction Capacity (Kw)", styles: { ...commonCell, fontStyle: "bold" } },
          { content: data.sanctionCapacity || "3", styles: { ...commonCell, fontStyle: "bold" } }
        ],
        
        // Row 9
        [
          { content: "9", styles: { ...commonCell, halign: "center" } },
          { content: "Project Model", styles: { ...commonCell } },
          { content: "Capex", styles: { ...commonCell } }
        ],
        
        // Row 10
        [
          { content: "10", styles: { ...commonCell, halign: "center" } },
          { content: "RE Installed Capacity (Rooftop)", styles: { ...commonCell, fontStyle: "bold" } },
          { content: data.installedCapacity || "3", styles: { ...commonCell, fontStyle: "bold" } }
        ],
        
        // Row 11
        [
          { content: "11", styles: { ...commonCell, halign: "center" } },
          { content: "RE Installed Capacity (Rooftop+Ground)", styles: { ...commonCell } },
          { content: data.installedCapacityRooftopGround || "NA", styles: { ...commonCell } }
        ],
        
        // Row 12
        [
          { content: "12", styles: { ...commonCell, halign: "center" } },
          { content: "RE Installed Capacity (Ground)", styles: { ...commonCell } },
          { content: data.installedCapacityGround || "NA", styles: { ...commonCell } }
        ],
        
        // Row 13
        [
          { content: "13", styles: { ...commonCell, halign: "center" } },
          { content: "Installation Date", styles: { ...commonCell, fontStyle: "bold" } },
          { content: data.installationDate || "02/01/2026", styles: { ...commonCell, fontStyle: "bold" } }
        ],
        
        // Row 14 - Solar PV Details
        [
          { content: "14", styles: { ...commonCell, halign: "center", valign: "top" } },
          { content: "Solar PV Details", styles: { ...commonCell, fontStyle: "bold", valign: "top" } },
          { 
            content: [
              `Inverter Capacity (Kw): ${data.inverterCapacity || "3"}`,
              `Inverter Make: ${data.inverterMaker || "Growatt 3KW TL - X2 (Pro)"}, ${data.inverterSerial || "0SQQD27V26D800YD"}`,
              `No. of PV Modules: ${data.noOfPVModules || data.numberOfModules || "6"}`,
              `Module Capacity (kw): ${data.moduleCapacity || `${data.sanctionCapacity || "3"}KW`}`
            ].join("\n"),
            styles: { ...commonCell, valign: "top" } 
          }
        ]
      ];

      // Render main table
      autoTable(doc, {
        startY: 45,
        margin: { left: marginL, right: marginR },
        theme: "grid",
        styles: commonCell,
        columnStyles: {
          0: { cellWidth: col0, halign: "center" },
          1: { cellWidth: col1, halign: "left" },
          2: { cellWidth: col2, halign: "left" },
        },
        head: [bodyRows[0]],
        body: bodyRows.slice(1),
      });

      // ==================== PAGE 2: Proforma-A with Signatures ====================
      doc.addPage();
      let y = 25;

      // Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Proforma-A", pageW / 2, y, { align: "center" });
      
      y += 10;
      doc.setFontSize(12);
      doc.text("COMMISSIONING REPORT (PROVISIONAL) FOR GRID CONNECTED SOLAR", pageW / 2, y, { align: "center" });
      
      y += 7;
      doc.text("PHOTOVOLTAIC POWER PLANT (with Net-metering facility)", pageW / 2, y, { align: "center" });
      
      y += 15;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      
      // Certificate text
      const certText = `Certified that a Grid Connected SPV Power Plant of ${data.totalCapacity || data.sanctionCapacity || ""} KWp capacity has been installed at the ${addressFull || ""} which has been installed by Niyati Multi Services on ${data.installationDate || ""}. The system is as per BIS/MNRE specifications. The system has been checked for its performance and found in order for further commissioning.`;
      
      const wrappedCert = doc.splitTextToSize(certText, usableW - 20);
      doc.text(wrappedCert, marginL + 10, y, { lineHeightFactor: 1.5 });
      
      y += wrappedCert.length * 7 + 20;

      // ==================== SIGNATURE SECTION ====================
      
      // Left side - Beneficiary Signature
      const leftX = marginL + 15;
      const rightX = pageW - marginR - 95;

      // Add beneficiary signature image
      if (signImageData) {
        doc.addImage(signImageData, "PNG", leftX, y - 8, 35, 12);
      }
      
      // Add beneficiary signature text
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("Signature of the beneficiary", leftX, y + 8);
      doc.text(data.customerName || "", leftX, y + 14);

      // Right side - Agency with seal and signature
      if (signImageData) {
        doc.addImage(signImageData, "PNG", rightX, y - 8, 35, 12);
      }
      
      if (sealImageData) {
        // Position seal to the right of signature
        doc.addImage(sealImageData, "JPEG", rightX + 40, y - 15, 28, 28);
      }
      
      // Add agency text
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("Signature of the agency with name, seal and date", rightX, y + 8);
      doc.text("Niyati Multi Services", rightX, y + 14);

      y += 30;

      // ==================== MSEDCL SECTION ====================
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      
      const msedclText = "The above RTS installation has been inspected by me for Pre-Commissioning Testing of Roof Top Solar Connection on dt. ____________________ as per guidelines issued by the office of The Chief Engineer vide letter no ____________________ on dt. ____________________ and found in order for commissioning.";
      
      const wrappedMsedcl = doc.splitTextToSize(msedclText, usableW - 20);
      doc.text(wrappedMsedcl, marginL + 10, y, { lineHeightFactor: 1.5 });
      
      y += wrappedMsedcl.length * 7 + 15;

      // ==================== MSEDCL OFFICER SIGNATURE ====================
      
      doc.setFont("helvetica", "bold");
      doc.setFontSize(12);
      doc.text("Signature of the MSEDCL Officer", marginL + 10, y);
      
      y += 12;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      
      // Left column
      doc.text("Name: _________________________________", marginL + 10, y);
      doc.text("Designation: ___________________________", marginL + 10, y + 10);
      
      // Right column
      doc.text("Date: _________________________________", marginL + 120, y);
      doc.text("Seal: _________________________________", marginL + 120, y + 10);

      // Footer
      doc.setFontSize(8);
      doc.setTextColor(100);
      doc.text(`Generated on: ${data.generatedDate} | Consumer No: ${data.consumerNo || ""}`, pageW / 2, pageH - 15, { align: "center" });

      // Save PDF
      const fileName = `Commissioning_Report_${data.consumerNo || "Customer"}_${dayjs().format("YYYYMMDD")}.pdf`;
      doc.save(fileName);

      message.success("Commissioning Report generated successfully!");
    } catch (error) {
      dbg("PDF generation error:", error);
      message.error("Failed to generate PDF: " + error.message);
    } finally {
      setGenerating(false);
    }
  };

  const columns = [
    {
      title: "Select",
      dataIndex: "id",
      key: "select",
      render: (id) => (
        <Checkbox
          onChange={(e) => handleOrderSelect(id, e.target.checked)}
          checked={selectedOrders.some((order) => order.id === id)}
        />
      ),
      width: 60,
    },
    { 
      title: "Order ID", 
      dataIndex: "id", 
      key: "id" 
    },
    { 
      title: "Customer", 
      dataIndex: ["customer", "customerName"], 
      key: "customerName",
      render: (text) => text || "N/A"
    },
    {
      title: "Date",
      dataIndex: "orderDate",
      key: "orderDate",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "N/A"),
    },
    {
      title: "Plant Size",
      dataIndex: "plantSizeKw",
      key: "plantSizeKw",
      render: (size) => (size ? `${size} KW` : "N/A"),
    },
  ];

  return (
    <Modal
      title={
        <span>
          <FilePdfOutlined /> Generate Commissioning Report
        </span>
      }
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="generate"
          type="primary"
          icon={<DownloadOutlined />}
          onClick={generatePDF}
          loading={generating}
          disabled={selectedOrders.length === 0}
        >
          {generating ? "Generating..." : "Download PDF"}
        </Button>,
      ]}
    >
      <div style={{ padding: "12px 0" }}>
        <Card size="small" title="Select Order">
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Checkbox
              onChange={(e) => handleSelectAll(e.target.checked)}
              indeterminate={
                selectedOrders.length > 0 &&
                selectedOrders.length < (orders?.length || 0)
              }
              checked={
                (orders?.length || 0) > 0 &&
                selectedOrders.length === (orders?.length || 0)
              }
            >
              Select All
            </Checkbox>
            {loadingAssets && <span style={{ color: "#722ed1" }}>Loading assets...</span>}
          </div>
          
          <Table
            columns={columns}
            dataSource={orders}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 5 }}
            scroll={{ y: 250 }}
          />
        </Card>

        {/* Hidden form fields */}
        <Form form={form} style={{ marginTop: 16, display: "none" }}>
          <Form.Item name="customerName" />
          <Form.Item name="consumerNo" />
          <Form.Item name="mobileNumber" />
          <Form.Item name="email" />
          <Form.Item name="address" />
          <Form.Item name="pincode" />
          <Form.Item name="district" />
          <Form.Item name="state" />
          <Form.Item name="reArrangementType" />
          <Form.Item name="reSource" />
          <Form.Item name="projectModel" />
          <Form.Item name="installedCapacityRooftopGround" />
          <Form.Item name="installedCapacityGround" />
          <Form.Item name="sanctionCapacity" />
          <Form.Item name="installedCapacity" />
          <Form.Item name="installationDate" />
          <Form.Item name="moduleMake" />
          <Form.Item name="moduleModel" />
          <Form.Item name="moduleWattage" />
          <Form.Item name="numberOfModules" />
          <Form.Item name="totalCapacity" />
          <Form.Item name="warrantyDetails" />
          <Form.Item name="inverterMaker" />
          <Form.Item name="inverterCapacity" />
          <Form.Item name="inverterRating" />
          <Form.Item name="mppt" />
          <Form.Item name="hpd" />
          <Form.Item name="manufacturingYear" />
          <Form.Item name="panelWarrantyYears" />
          <Form.Item name="inverterWarrantyYears" />
          <Form.Item name="plantWarrantyYears" />
          <Form.Item name="noOfPVModules" />
          <Form.Item name="moduleCapacity" />
          <Form.Item name="inverterSerial" />
        </Form>
      </div>
    </Modal>
  );
};

export default ProformaGenerator;